import { appSync } from '../app/app_sync.js';
import { log, assert } from '../app/logger.js';
import { HTMLControl } from '../controls/html_control.js';
import { UILoader, animateUILoading } from '../ui/ui_loader.js';
import { reportNoLocError } from '../ui/report_error.js';
import { AsapPromise } from '../util/asap_promise.js';
import { listenToEvent } from '../util/listen_to_event.js';
import { screenMedia } from '../util/util.js';
import { Coords } from './coords.js';
import { SwapMarker } from './swap_marker.js';

import '../location/loc_edit.js';
import '../location/loc_view_regular.js';
import '../location/loc_view_shorty.js';

/**
 * Location info map side panel.
 */
class MapLocStickPanel extends HTMLControl {
  static markup = `
    <loc-view-regular loadable hidden></loc-view-regular>
    <loc-view-shorty hidden></loc-view-shorty>
    <loc-edit hidden></loc-edit>
  `;

  static selectors = {
    EditView: 'loc-edit',
    RegularView: 'loc-view-regular',
    ShortyView: 'loc-view-shorty',
  };

  static elements = {
    editView: this.selectors.EditView,
    regularView: this.selectors.RegularView,
    shortyView: this.selectors.ShortyView,
  };

  static tagName = 'map-loc-stick-panel';

  constructor() {
    super();

    this.#listenTo({
      closerequest: () => this.close(),
      collapse: () => this.#activateView(this.shortyView),
      edit: () => this.#locEdit(),
      editcancel: () => this.#locEditCancelled(),
      expand: () => this.#activateView(this.regularView),
      locchange: ev => this.#locChanged(ev.detail),
      locdelete: () => this.#locDeleted(),
      locposchange: ev => this.#locPosChanged(ev.detail),
    });
  }

  initialize() {
    super.initialize();

    if (this.map) {
      this.editView.map = this.map;
    }
  }

  setup(map) {
    assert(map, `[map:sidepanel] no map given`);

    this.map = map;

    this.swapMarker = new SwapMarker(this.map, {
      name: 'locmapsidepanel-swapmarker',
      onmove: ev => {
        // Update coordinate fields in edit mode, when marked is dragged.
        assert(
          !this.editView.hidden,
          `[map:marker] MapLocInfo no marker move in non edit mode`
        );
        log(
          `[map:marker] MapLocInfo marker is dragged to (${ev.lat}, ${ev.lng})`
        );
        this.editView.setCoordinates(ev);
      },
      onaccept: () => this.regularView.fireLocPick(),
    });

    if (this.isInitialized) {
      this.editView.map = map;
    }
  }

  show(loc, options) {
    // Ignore this show if the panel is shown for this marker already.
    // TODO: check locations having no markers.
    if (this.swapMarker.isSwappedFor(loc.marker)) {
      return;
    }

    log(`[map:sidepanel] schedule MapLocInfo show`);

    // If a show is scheduled, then cancel it to make sure next show is scheduled
    // for given marker.
    this.cancelShow();

    // Give enough time for a double click on a marker.
    this.#timeoutId = setTimeout(() => {
      // Another cycle if the panel is not yet bound to the maps.
      if (!this.isConnected) {
        this.cancelShow();
        this.show(loc, options);
        return;
      }

      // Show the panel.
      this.showSync(loc, options);
      this.#timeoutId = 0;
    }, 300);
  }

  async showSync(loc, { showExpanded, panAndZoom } = {}) {
    log(
      `[map:sidepanel] show <map-loc-stick-panel/> for '${loc.name}':${
        loc.id || 'no-id'
      } (${loc.lat}, ${loc.lng})`
    );
    assert(
      !this.swapMarker.isSwappedFor(loc.marker),
      '[map:sidepanel] <map-loc-stick-panel/> was shown twice for same location'
    );

    this.#loc = loc;

    if (loc.id) {
      appSync.syncLoc.connect(loc.id);
    }

    this.#clearDataUpdatePromise();

    let loadingPromise = loc
      .loadIfNeeded()
      .then(
        () =>
          loc.notFound &&
          new Promise(resolve =>
            reportNoLocError(() => Promise.resolve(this.close()).then(resolve))
          )
      );
    animateUILoading(this, loadingPromise);

    this.#isShown = true;
    this.#fireShow();

    let view = loc.id
      ? showExpanded || !screenMedia.isMobile() || !this.regularView.hidden
        ? this.regularView
        : this.shortyView
      : this.editView;
    this.#activateView(view);

    await loadingPromise;

    // Closed or reshown.
    if (!this.#isShown || this.#loc != loc) {
      return;
    }

    // Swap with a temporary marker if the location has no associated routes,
    // otherwise release the temporary marker if any.
    loc.routes.length == 0
      ? this.swapMarker.swapFor(loc.marker, loc)
      : this.swapMarker.release();

    if (loc.gpxTracks) {
      for (let gpxTrack of loc.gpxTracks) {
        gpxTrack.active = true;
      }
    }

    return panAndZoom ? this.map.panAndZoomTo(loc) : this.#moveMapIfNeeded(loc);
  }

  isShown() {
    return this.#isShown;
  }

  cancelShow() {
    if (this.#timeoutId) {
      log(`[map:sidepanel] cancel show for <map-loc-stick-panel/>`);
      clearTimeout(this.#timeoutId);
      this.#timeoutId = 0;
    }
  }

  close(locRemoved) {
    if (!this.#isShown) {
      return;
    }
    log(`[map:sidepanel] close <map-loc-stick-panel/>`);

    this.#clearDataUpdatePromise();

    if (this.data && this.data.gpxTracks) {
      for (let gpxTrack of this.data.gpxTracks) {
        gpxTrack.active = false;
      }
    }

    this.supress(locRemoved);

    this.#isShown = false;
    this.#fireClose();
  }

  supress(locRemoved) {
    this.cancelShow();
    this.swapMarker.release(locRemoved);
  }

  closeIfOpen() {
    this.close();
  }

  get locInfo() {
    return this.#loc;
  }

  #locChanged({ locData }) {
    this.#locEditOff();
    if (locData.id) {
      this.#moveMapIfNeeded(locData);
    } else {
      // Enter the loading state unitl a new location is added and
      // the panel is reshown (see 'locchanged' event handler in map_panel.js).
      animateUILoading(this, this.#getDataUpdatePromise(), UILoader.NoTimeout);
    }
    this.swapMarker.position = locData;
  }

  #locDeleted() {
    this.close('doNotShowOriginalMarker');
  }

  #locPosChanged({ lat, lng }) {
    log(`[map:sidepanel] MapLocInfo position was updated to (${lat}, ${lng})`);
    let position = { lat, lng };
    this.swapMarker.position = position;
    this.#moveMapIfNeeded(position);
  }

  #locEdit() {
    this.#activateView(this.editView);
    this.swapMarker.draggable = true;
  }

  #locEditCancelled() {
    this.#locEditOff();
    if (this.swapMarker.origMarker) {
      this.swapMarker.position = this.swapMarker.origMarker.position;
    }
    if (!this.#loc.id) {
      this.close();
    }
  }

  #locEditOff() {
    this.#activateView(this.regularView);
    this.swapMarker.draggable = false;
  }

  /**
   * Move map to show the location.
   */
  async #moveMapIfNeeded(loc) {
    // If the panel overlaps with the location, then center the map at
    // the location.
    let [loc_x, loc_y] = await Coords.toClient(this.map, loc.lat, loc.lng);

    let mapRect = this.#mapElement.getBoundingClientRect();
    let panelRect = this.parentNode.getBoundingClientRect();

    // Panel rect may be at left or at bottom of the map.
    let left = panelRect.right < mapRect.right ? panelRect.right : mapRect.left;
    let right = mapRect.right;
    let bottom =
      panelRect.right < mapRect.right ? mapRect.bottom : panelRect.top;
    let top = mapRect.top;

    if (loc_x < left || loc_x > right || loc_y < top || loc_y > bottom) {
      log(
        `[map:sidepanel] center map at the location (${loc.lat}, ${loc.lng})`
      );
      this.map.panTo({ lat: loc.lat, lng: loc.lng });
    }
  }

  get activeView() {
    return this.#views.find(v => !v.hidden);
  }

  #activateView(viewToActivate) {
    viewToActivate == this.shortyView
      ? this.classList.add('shorty-view')
      : this.classList.remove('shorty-view');

    for (let view of this.#views) {
      if (view == viewToActivate) {
        view.set(this.#loc);
      }
      view.hidden = view != viewToActivate;
    }
  }

  #listenTo(map) {
    for (let [name, listener] of Object.entries(map)) {
      listenToEvent(this, name, listener);
    }
  }

  #fireClose() {
    this.dispatchEvent(new Event('close'));
  }

  #fireShow() {
    this.dispatchEvent(
      new CustomEvent('show', {
        detail: this.#loc,
      })
    );
  }

  #getDataUpdatePromise() {
    this.#clearDataUpdatePromise();

    log(`[map:sidepanel] initialize data loading animation`);
    return (this.#dataUpdatePromise = new AsapPromise());
  }

  #clearDataUpdatePromise() {
    if (this.#dataUpdatePromise) {
      log(`[map:sidepanel] stop data loading animation`);
      this.#dataUpdatePromise.resolve();
      this.#dataUpdatePromise = null;
    }
  }

  get #views() {
    return [this.editView, this.regularView, this.shortyView];
  }

  get #mapElement() {
    return this.ownerDocument.getElementById('map');
  }

  #dataUpdatePromise;
  #loc;
  #timeoutId;
  #isShown = false;
}

MapLocStickPanel.defineCustomElement();

export { MapLocStickPanel };
